import { AsyncPipe, Location } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, inject, OnInit, signal, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import {
  BbitArray,
  BbitI18n,
  BbitObject,
  BbitString,
  IBbitI18n,
  IBbitWebShopItem,
  IBbitWebShopItemMetaResponse,
  IBbitWebShopItemMetaResponseWithItem,
} from '@bbitgmbh/bbit.utils';
import {
  BbitWebShopAlgoliaService,
  BbitWebShopApiService,
  BbitWebShopCartService,
  BbitWebShopFormatPricePipe,
  BbitWebShopFormatUnitPipe,
  BbitWebShopRichTextI18nPipe,
  BbitWebShopService,
  BbitWebShopVatCalculationMethod,
} from '@bbitgmbh/webshop.sdk';
import {
  BbitPictureSrcDirective,
  BbitStructuredDataService,
  BbitWebsiteMetaDataService,
  BbitWebsiteService,
  BbitWebsiteTrackingService,
  I18nPipe,
} from '@bbitgmbh/website.sdk';
import { NgIcon } from '@ng-icons/core';
import { BehaviorSubject, distinctUntilChanged, filter, map, switchMap, timer } from 'rxjs';
import { BbitImageOverlayItem, BbitImageOverlayItemType } from '../../helper/image-overlay/image-overlay.ref';
import { ImageSliderComponent } from '../../helper/image-slider/image-slider.component';
import { ItemAttributeSortPipe } from '../../helper/item-attribute-sort/item-attribute-sort.pipe';
import { ItemPriceComponent } from '../../helper/item-price/item-price.component';
import { ItemStockComponent } from '../../helper/item-stock/item-stock.component';
import { LoadingComponent } from '../../helper/loading/loading.component';
import { QuantityInputComponent } from '../../helper/quantity-input/quantity-input.component';
import { ReadMoreComponent } from '../../helper/read-more/read-more.component';
import { IBreadcrumb } from '../item-list-header/item-list-header.component';

@Component({
  selector: 'app-item-detail',
  templateUrl: './item-detail.component.html',
  styleUrls: ['./item-detail.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    LoadingComponent,
    NgIcon,
    RouterLink,
    ImageSliderComponent,
    ItemStockComponent,
    ItemPriceComponent,
    QuantityInputComponent,
    I18nPipe,
    BbitWebShopFormatUnitPipe,
    BbitWebShopFormatPricePipe,
    AsyncPipe,
    ItemAttributeSortPipe,
    BbitWebShopRichTextI18nPipe,
    BbitPictureSrcDirective,
    ReadMoreComponent,
  ],
})
export class ItemDetailComponent implements OnInit {
  api = inject(BbitWebShopApiService);
  cart = inject(BbitWebShopCartService);
  website = inject(BbitWebsiteService);
  webShop = inject(BbitWebShopService);
  private _algolia = inject(BbitWebShopAlgoliaService);
  private _router = inject(Router);
  private _route = inject(ActivatedRoute);
  private _location = inject(Location);
  private _meta = inject(BbitWebsiteMetaDataService);
  private _structuredData = inject(BbitStructuredDataService);
  private _tracking = inject(BbitWebsiteTrackingService);

  public keys = Object.keys;
  public currentSlug = new BehaviorSubject<string | undefined>(undefined);
  public isLoading = new BehaviorSubject<boolean>(false);
  public mainItemSubject = new BehaviorSubject<IBbitWebShopItem | undefined>(undefined);
  public selectedItemSubject = new BehaviorSubject<IBbitWebShopItem | undefined>(undefined);
  public selectedMetaSubject = new BehaviorSubject<IBbitWebShopItemMetaResponse | undefined>(undefined);
  public mainMetaSubject = new BehaviorSubject<IBbitWebShopItemMetaResponse | undefined>(undefined);
  public variants = new BehaviorSubject<IBbitWebShopItemMetaResponseWithItem[]>([]);
  public itemNotFound = new BehaviorSubject(false);
  public itemsToAdd = new BehaviorSubject(1);
  public VatCalculationMethod = BbitWebShopVatCalculationMethod;
  public selectedAttributes = new BehaviorSubject<{ [key: string]: string }>({});
  public accessories = new BehaviorSubject<IBbitWebShopItemMetaResponse[]>([]);
  public crossSellingItems = new BehaviorSubject<IBbitWebShopItemMetaResponse[]>([]);
  public downloads = new BehaviorSubject<{ designation: IBbitI18n; url?: string; fileName: string }[]>([]);
  public breadcrumbs = new BehaviorSubject<IBreadcrumb[]>([]);
  private _infoBox: ElementRef<HTMLElement> | undefined;
  public technicalData = new BehaviorSubject<{ text: string; value: string }[]>([]);
  public showAllTechnicalData = signal(false);
  public showInfoBoxTitle = new BehaviorSubject(false);
  public imagesAndVideos = signal<BbitImageOverlayItem[]>([]);
  @ViewChild('infoBox') set infoBox(infoBox: ElementRef<HTMLElement>) {
    this._infoBox = infoBox;
    this.initializeIntersectionObserver();
  }
  private _itemTitle: ElementRef<HTMLElement> | undefined;
  @ViewChild('itemTitle') set itemTitle(itemTitle: ElementRef<HTMLElement>) {
    this._itemTitle = itemTitle;
    this.initializeIntersectionObserver();
  }

  // this will delay the spinner on loading for 250ms
  debouncedLoading = this.isLoading.asObservable().pipe(
    distinctUntilChanged(),
    switchMap((isLoading) => (isLoading ? timer(250) : timer(0)).pipe(map(() => isLoading))),
    takeUntilDestroyed(),
  );

  constructor() {
    // http://localhost:4300/item/%23K6B7GyyjriaFkXNbhKhNY

    this._route.params.pipe(takeUntilDestroyed()).subscribe((params) => {
      this.currentSlug.next(params['slug']);
    });

    this.itemNotFound.pipe(takeUntilDestroyed()).subscribe((notFound) => {
      if (notFound) {
        this._router.navigate(['/404-not-found']);
      }
    });

    this.mainItemSubject.pipe(takeUntilDestroyed()).subscribe((item: IBbitWebShopItem | undefined) => {
      const keys = Object.keys(item?.resolvedCategories || {});

      // Filter keys that match the pattern `lvlX` where X is a digit and convert to numbers
      const levels = keys
        .filter((key) => key.startsWith('lvl') && !Number.isNaN(Number(key.slice(3))))
        .map((key) => Number(key.slice(3)));

      // Find the highest level
      const highestLevel = levels.length ? Math.max(...levels) : null;
      const highestCategories = item?.resolvedCategories?.[`lvl${highestLevel}`] || [];
      const highestCategory = highestCategories[0];

      const categories = highestCategory?.split(' > ');

      const breadcrumbs: IBreadcrumb[] = [];

      const categoryTree = [];

      if (categories?.length > 0) {
        const allCategories = this.webShop.getCategories();

        for (const o of categories) {
          categoryTree.push(o);
          const category = allCategories.find(
            (c) => BbitString.stripBeginningHashtag(c._id) === BbitString.stripBeginningHashtag(o),
          );

          breadcrumbs.push({
            facet: `categories.lvl${breadcrumbs.length}`,
            text: { object: category?.designation || {} },
            value: categoryTree.join(' > '),
          });
        }

        this.breadcrumbs.next(breadcrumbs);
      }

      if (breadcrumbs.length > 0) {
        breadcrumbs.unshift({
          facet: undefined,
          text: { key: 'allProducts' },
        });
      }

      this.setImagesAndVideos();
    });

    this.currentSlug
      .asObservable()
      .pipe(
        map((s) => {
          if (s) {
            this.itemNotFound.next(false);
            this.isLoading.next(true);
          } else {
            this.isLoading.next(false);
            this.itemNotFound.next(true);
            console.error('No slug provided');
          }
          return s;
        }),
        filter((s) => !!s),
        switchMap(async (slug) => {
          try {
            // biome-ignore lint/style/noNonNullAssertion: required
            await this.loadItemBySlug(slug!);
          } catch (err) {
            console.error('Item not found', err);
            this.itemNotFound.next(true);
          }
          return slug;
        }),
        map((s) => {
          this.isLoading.next(false);
          return s;
        }),
        takeUntilDestroyed(),
      )
      .subscribe();

    this.itemsToAdd.pipe(takeUntilDestroyed()).subscribe((amount) => {
      if (this.selectedMetaSubject.value?.price?.graduatedPrices) {
        // biome-ignore lint/correctness/noUnsafeOptionalChaining: required
        for (const graduatedPrice of this.selectedMetaSubject.value?.price.graduatedPrices) {
          if (
            amount >= (graduatedPrice.quantity.amount || 0) &&
            amount <= ((graduatedPrice as any).validUntil || 0) // TODO interfaces
          ) {
            (graduatedPrice as any).isActive = true; // TODO interfaces
          } else {
            (graduatedPrice as any).isActive = false; // TODO interfaces
          }
        }
        this.selectedMetaSubject.next(this.selectedMetaSubject.value);
      }
    });

    this.selectedItemSubject.pipe(takeUntilDestroyed()).subscribe((item) => {
      if (item) {
        this._tracking.itemView({
          item: { _id: item._id, itemNumber: item.itemNumber, designation: { object: item?.designation } },
        });

        const allDownloads = BbitArray.uniqueBy(
          [...(this.mainItemSubject.value?.downloads || []), ...(item?.downloads || [])],
          (o) => o.file?.url,
        );

        const downloads = allDownloads
          .filter((o) => {
            const languages: string[] = o?.languages || [];
            if (languages?.length > 0) {
              if (
                !languages.find(
                  (l) =>
                    BbitString.ensureBeginningHashtag(l) === BbitString.ensureBeginningHashtag(BbitI18n.getLanguage()),
                )
              ) {
                return false;
              }
            }
            if (!o?.file?.url) {
              return false;
            }
            if (!o.designation) {
              return false;
            }
            return true;
          })
          .map((o) => {
            const fileName = o.file?.fileName || o.file?.url?.split('/').pop() || '';
            return {
              designation: o.designation ? { object: o.designation } : 'Download',
              url: o?.file?.url,
              fileName,
            };
          });

        this.downloads.next(downloads);

        this.setImagesAndVideos();
      }
    });

    this.selectedMetaSubject.pipe(takeUntilDestroyed()).subscribe((meta) => {
      this.technicalData.next(
        (meta?.displayAttributes || []).map((o) => {
          return { text: BbitI18n.t(o.text), value: BbitI18n.t(o.value) };
        }),
      );

      this.accessories.next(
        BbitArray.uniqueBy(
          [...(this.mainMetaSubject.value?.accessories || []), ...(meta?.accessories || [])],
          (o) => o._id,
        ),
      );

      this.crossSellingItems.next(
        BbitArray.uniqueBy(
          [...(this.mainMetaSubject.value?.crossSellingItems || []), ...(meta?.crossSellingItems || [])],
          (o) => o._id,
        ),
      );
    });
  }

  public ngOnInit(): void {
    window.scrollTo(0, 0);
  }

  public getImages(): { file: { url: string } }[] {
    const images = [
      ...(this.selectedItemSubject.value?.images || []).filter(Boolean),
      ...(this.mainItemSubject.value?.images || []).filter(Boolean),
    ];
    return images as { file: { url: string } }[];
  }

  public initializeIntersectionObserver(): void {
    if (!this._infoBox?.nativeElement || !this._itemTitle?.nativeElement) {
      return;
    }

    window.addEventListener('scroll', () => {
      const infoBoxRect = this._infoBox?.nativeElement?.getBoundingClientRect?.();
      const infoBoxY = (infoBoxRect?.y || 0) + (infoBoxRect?.height || 0);
      const itemTitleY = this._itemTitle?.nativeElement?.getBoundingClientRect?.()?.y || 0;
      this.showInfoBoxTitle.next(infoBoxY > itemTitleY);
    });
  }

  public breadcrumbClicked(item: IBreadcrumb) {
    if (!item.facet) {
      this._algolia.clearCategoryFilters();
    } else {
      this._algolia.setCategoryFilter(item.facet, item.value || '');
    }
    if (window.location.pathname !== '/shop/items') {
      this._router.navigate(['/shop/items'], { queryParamsHandling: 'merge' });
    }
  }

  public async loadItemBySlug(slug: string): Promise<void> {
    let slugToChooseAsVariant = '';
    let item = await this.api.getItemBySlug(slug);

    // load main item if required
    if (item.productVariantGroup) {
      item = await this.api.getItemById(item.productVariantGroup);
      slugToChooseAsVariant = slug;
    }

    // http://localhost:4300/shop/item/d5-3460-od-l
    const meta = await this.api.getItemMeta(item._id);

    if (item.itemType === '#productGroup') {
      const variants = await Promise.all(
        (meta.variants || []).map((o) =>
          (async () => {
            const variant = await this.api.getItemById(o._id);
            return {
              ...o,
              item: variant,
            };
          })(),
        ),
      );
      this.variants.next(variants);

      let selectedVariant = variants[0];
      if (slugToChooseAsVariant) {
        const found = variants.find((o) => o.item._slug === slugToChooseAsVariant);
        if (found) {
          selectedVariant = found;
        }
      }

      if (selectedVariant) {
        this._selectVariant(selectedVariant);
      }
    } else {
      this.selectedItemSubject.next(item);
      this.selectedMetaSubject.next(meta);
    }

    this.accessories.next(
      BbitArray.uniqueBy(
        [...(meta.accessories || []), ...(this.variants.value.flatMap((o) => o.accessories || []) || [])],
        (o) => o._id,
      ),
    );

    this.mainItemSubject.next(item);
    this.mainMetaSubject.next(meta);
    this._structuredData.setProduct(item, meta);
    this._meta.update({
      title: { object: item?.seo?.title },
      description: { object: item?.seo?.description },
      keywords: { object: item?.seo?.keywords },
    });
  }

  public isAttributeAvailable(attributeId: string, configId: string): boolean {
    return !!this.variants.value.find((o) => {
      return o.attributes?.[attributeId] === configId;
    });
  }

  public selectAttribute(attributeId: string, configId: string): void {
    let variant = this.variants.value.find((o) => {
      for (const i of Object.keys(this.selectedAttributes.value || {})) {
        if (i === attributeId) {
          continue;
        }
        if (o.attributes?.[i] !== this.selectedAttributes.value[i]) {
          return false;
        }
      }
      return o.attributes?.[attributeId] === configId;
    });
    if (!variant) {
      // find first variant with the given attribute
      variant = this.variants.value.find((o) => {
        if (o.attributes?.[attributeId] === configId) {
          return true;
        }
        return false;
      });
    }
    if (!variant) {
      return;
    }
    this._selectVariant(variant);
  }

  private _selectVariant(variant: IBbitWebShopItemMetaResponseWithItem): void {
    const url = this._router
      .createUrlTree([], {
        relativeTo: this._route,
      })
      .toString();
    const urlParts = url.split('/');
    urlParts[urlParts.length - 1] = variant.item._slug;
    this._location.replaceState(urlParts.join('/'));
    this.selectedItemSubject.next(variant.item);
    this.selectedMetaSubject.next(variant);
    this.selectedAttributes.next(BbitObject.clone(variant.attributes || {}));
    this.itemsToAdd.next(1);
  }

  public setImagesAndVideos() {
    const images: BbitImageOverlayItem[] = [];
    const allImages = [
      ...(this.selectedItemSubject.value?.images || []),
      ...(this.mainItemSubject.value?.images || []),
    ];
    for (const o of allImages) {
      if (o?.file?.url) {
        images.push({
          type: BbitImageOverlayItemType.IMAGE,
          url: o.file.url,
        });
      }
    }
    const allVideos = [
      ...(this.selectedItemSubject.value?.videos || []),
      ...(this.mainItemSubject.value?.videos || []),
    ];
    for (const o of allVideos) {
      if (o?.youTubeId || o?.vimeoId) {
        images.push({
          type: BbitImageOverlayItemType.VIDEO,
          youTubeId: o.youTubeId,
          vimeoId: o.vimeoId,
        });
      }
    }

    this.imagesAndVideos.set(images);
  }
}
